import {
  type MediaCardType,
  mediaCardTypeToKeys,
} from '@wandb/weave/common/types/media';
import {isNotNullOrUndefined} from '@wandb/weave/common/util/types';
import {Select} from '@wandb/weave/components/Form/Select';
import React, {useMemo, useState} from 'react';
import {type MultiValue} from 'react-select';

import {MetricSearchErrorNotice} from '../../../../MetricSearch/MetricSearchErrorNotice';
import {useMetricSearchState} from '../../../../MetricSearch/useMetricSearchState';
import {useUpdateConfigWithMediaKeys} from '../../../../PanelMediaBrowser/hooks/useUpdateConfigWithMediaKeys';
import {
  SUPPORTED_MEDIA_DRAWER_TYPE_STRINGS,
  SUPPORTED_MEDIA_DRAWER_TYPES,
} from '../../../../PanelMediaBrowser/supportedMediaDrawerTypes';
import {isValidMediaKeyOptionForMetric} from '../../../../PanelMediaBrowser/utils/isValidMediaKeyOptionForMetric';
import {mediaCardTypeForMetric} from '../../../../PanelMediaBrowser/utils/mediaCardTypeForMetric';
import {usePanelMediaKeys} from '../../hooks';
import {useMediaSettingsContext} from '../contexts/MediaSettingsContextProvider';
import {
  isResultLimitHintOption,
  MEDIA_KEY_RESULT_LIMIT,
  MediaKeyMultiValue,
  type MediaKeyOption,
  resultLimitHintOption,
} from './mediaKeySelectShared';

/**
 * Media key picker backed by metric search, for the NGW path.
 *
 * Mirrors `MediaKeys` — same react-select markup and alias chips — but sources
 * options from the metric search API instead of `useMediaKeys`, whose key info
 * NGW leaves empty.
 */
export const MediaKeysMetricSearch = () => {
  const {config, updateConfig, panelRef, mediaCardTypes} =
    useMediaSettingsContext();
  const mediaKeys = usePanelMediaKeys(panelRef);
  const updateConfigWithMediaKeys = useUpdateConfigWithMediaKeys();
  // Local state so the dropdown updates without waiting for updateConfig.
  const [keys, setKeys] = useState(mediaKeys ?? []);
  const [pickedCardType, setPickedCardType] = useState<MediaCardType | null>(
    null
  );

  // Stands in for legacy's `keyTypes[keys[0]]`, which NGW leaves empty. Two
  // sources because the two kinds of selection differ: a clicked search result
  // carries its own `cardType`, while one restored from a saved config is a
  // bare key name, so fall back to the type the provider already resolved.
  //
  // Empty selection is null so the filter widens. Clearing never persists —
  // applyUpdateConfigWithMediaKeys drops an empty update and config keeps
  // reporting the old type — so without this you could not switch card type.
  const selectedCardType =
    keys.length === 0 ? null : (pickedCardType ?? mediaCardTypes?.[0] ?? null);

  // A media panel renders one card type at a time, so filter server-side: the
  // server truncates to the page size before a client filter would run, which
  // would hide keys that exist. With nothing selected, ask for the drawer's
  // supported types — the legacy picker restricts its options the same way.
  const types = useMemo(
    () =>
      selectedCardType != null
        ? (mediaCardTypeToKeys(selectedCardType) as string[])
        : SUPPORTED_MEDIA_DRAWER_TYPE_STRINGS,
    [selectedCardType]
  );

  const {
    results,
    hasMore,
    isLoading,
    error,
    searchErrorMessage,
    retry,
    setSearch,
  } = useMetricSearchState({
    types,
    resultLimit: MEDIA_KEY_RESULT_LIMIT,
  });

  const options: MediaKeyOption[] = useMemo(
    function buildMediaKeyOptions() {
      const opts: MediaKeyOption[] = [];
      for (const metric of results) {
        // The server filter matches on `types` membership and is wider than the
        // cards the panels draw, so a result can match the query and still not
        // be offerable. Same rule the popup picker applies.
        if (
          !isValidMediaKeyOptionForMetric(
            metric,
            selectedCardType,
            SUPPORTED_MEDIA_DRAWER_TYPES
          )
        ) {
          continue;
        }
        opts.push({
          value: metric.key,
          displayText: metric.key,
          cardType: mediaCardTypeForMetric(metric) ?? undefined,
        });
      }
      // Keep selections visible even when they're off the current result page.
      const seen = new Set(opts.map(o => o.value));
      for (const key of keys) {
        if (!seen.has(key)) {
          opts.push({value: key, displayText: key});
        }
      }
      // Last, so the hint sits at the bottom of the menu.
      if (hasMore) {
        opts.push(resultLimitHintOption(MEDIA_KEY_RESULT_LIMIT));
      }
      return opts;
    },
    [results, keys, selectedCardType, hasMore]
  );

  const selectedOptions = useMemo(
    () => keys.map(key => ({value: key, displayText: key})),
    [keys]
  );

  const onChangeMediaKeys = (value: MultiValue<MediaKeyOption>) => {
    // The hint row is disabled, but strip it defensively so it can never leak
    // into a selection and get persisted as a media key.
    const picked = value.filter(v => !isResultLimitHintOption(v));
    const newValues = picked.map(v => v.value).filter(isNotNullOrUndefined);

    setKeys(newValues);
    // Remember the first selection's type so the next query narrows to it, and
    // clear it when the selection empties so all types come back.
    setPickedCardType(
      newValues.length === 0 ? null : (picked[0]?.cardType ?? null)
    );
    updateConfigWithMediaKeys({mediaKeys: newValues}, config, updateConfig);
  };

  return (
    <div className="flex flex-col gap-4">
      <Select<MediaKeyOption, true>
        aria-label="Select Media Key"
        size="variable"
        value={selectedOptions}
        options={options}
        placeholder="Select Media Key"
        isMulti
        isSearchable
        isLoading={isLoading}
        // Results are already server-filtered by type and search term.
        filterOption={null}
        isOptionDisabled={isResultLimitHintOption}
        onInputChange={setSearch}
        closeMenuOnSelect={false}
        onChange={onChangeMediaKeys}
        getOptionLabel={opt => opt.displayText}
        components={{
          MultiValue: MediaKeyMultiValue,
        }}
      />
      <MetricSearchErrorNotice
        searchErrorMessage={searchErrorMessage}
        error={error}
        onRetry={retry}
      />
    </div>
  );
};
